import { ChangeEvent, memo } from 'react';
import { useTranslation } from 'react-i18next';

import { SVGIconType } from '../../../../constants/svgIcon';
import {
  getQuickCreateGameModeProps,
  QuickCreateGameMode,
} from '../../../../models/DraftStore';
import { WebBoosterType } from '../../../../models/UserStore';
import {
  AppBar,
  BubbleStepsProgressBar,
  PngIcon,
  SvgIcon,
  TextInput,
} from '../../../Molecules';

import styles from './CreateMemoryGameInfoSettingsTemplate.module.css';

type TextInputProps = {
  value: string;
  errorMessage?: string;
  maxLength: number;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
};

export type CreateMemoryGameInfoSettingsTemplateProps = {
  type: QuickCreateGameMode | undefined;
  webBoosterType: WebBoosterType | undefined;
  title: TextInputProps;
  expectedPayment: number;
  selectedItemCount: number;
  isNextDisabled: boolean;
  onClickHelpVideo: () => void;
  onPressSelectPractice: () => void;
  onClickBack: () => void;
  onClickNext: () => void;
};

export const CreateMemoryGameInfoSettingsTemplate = memo(
  ({
    type,
    webBoosterType,
    title,
    expectedPayment,
    selectedItemCount,
    isNextDisabled,
    onClickHelpVideo,
    onPressSelectPractice,
    onClickBack,
    onClickNext,
  }: CreateMemoryGameInfoSettingsTemplateProps) => {
    const { t } = useTranslation();
    return (
      <div className={styles.createMemoryInfoSettings}>
        <AppBar
          title={
            type
              ? t(getQuickCreateGameModeProps(type, webBoosterType).name)
              : ''
          }
          onClickBack={onClickBack}
          trailing={
            <>
              {expectedPayment !== 0 && (
                <div className={styles.paymentContainer}>
                  <PngIcon name={'CreationStamina'} width={24} height={24} />
                  <div className={styles.payTextContainer}>
                    {t('Pay: ')}
                    <p
                      style={{
                        whiteSpace: 'nowrap',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        marginLeft: '3px',
                        lineHeight: 'normal',
                      }}
                    >
                      {expectedPayment}
                    </p>
                  </div>
                  {/* {`${t('Pay: ')}${expectedPayment}`} */}
                </div>
              )}
            </>
          }
        />
        <div className={styles.userGuideBanner} onClick={onClickHelpVideo}>
          <SvgIcon
            className={styles.questionIcon}
            name={SVGIconType.QuestionHelp}
            color={'#333333'}
          />
          <div className={styles.bannerText}>{t('User Guide')}</div>
          <div className={styles.playerContainer}>
            <SvgIcon
              className={styles.playerIcon}
              name={SVGIconType.PlayerPlay}
              color={'white'}
            />
          </div>
        </div>
        <div className={styles.container}>
          <div className={styles.titleContainer}>
            <p className={styles.gameTypeName}>{t('Memory Practice')}</p>
          </div>
          <div className={styles.progress}>
            <BubbleStepsProgressBar currentStep={3} totalSteps={3} />
          </div>

          <div className={styles.albumNameContainer}>
            <p className={styles.titleName}>{t('Journey Name')}</p>
            <TextInput
              value={title.value}
              onChange={title.onChange}
              placeholder={t('Journey Name')}
              maxLength={title.maxLength}
              showMaxLength={true}
              errorMessage={title.errorMessage}
            />
          </div>

          <div className={styles.albumNameContainer}>
            <div
              className={styles.containerGray}
              onClick={onPressSelectPractice}
            >
              <SvgIcon
                name={SVGIconType.PracticeTick}
                className={styles.practiceIcon}
              />
              <p className={styles.textNormal}>{t('Select Memory Practice')}</p>
              <div className={styles.containerRight}>
                <p className={styles.textMinor}>{selectedItemCount}</p>
                <SvgIcon
                  name={SVGIconType.Arrow}
                  className={styles.arrowIcon}
                />
              </div>
            </div>
            {isNextDisabled ? (
              <p className={styles.errorMessage}>
                {t('This field is required.')}
              </p>
            ) : null}
          </div>
        </div>
        <div className={styles.bottomBar}>
          <div className={styles.nextContainer}>
            <button className={styles.nextButton} onClick={onClickBack}>
              <SvgIcon
                className={styles.leftArrowIcon}
                name={SVGIconType.Arrow}
              />
              <span className={styles.nextText}>{t('Back')}</span>
            </button>
            <button className={styles.nextButton} onClick={onClickNext}>
              <span className={styles.nextText}>{t('OK')}</span>
              <SvgIcon
                className={styles.rightArrowIcon}
                name={SVGIconType.Arrow}
              />
              {isNextDisabled ? <div className={styles.blackShadow} /> : null}
            </button>
          </div>
        </div>
      </div>
    );
  },
);
